import React from "react";
import { AlertTriangle, ArrowLeft, ArrowRight, Check, CheckCircle2, Loader2, Plus, Power } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { iconFor } from "@/components/Connectors/connectorIcons";
import type { Connector, ToolPermission } from "@/hooks/useConnectors";
import ToolPermissions from "@/components/Connectors/ToolPermissions";
import { actionFor, ctaLabelFor } from "@/utils/connectorActions";

// One connector, and every way into and out of it. The rule the whole screen is built around:
// nothing here is a dead end, so this pane always ends in something the user can press.

interface ConnectorDetailProps {
  connector: Connector;
  projectId?: string;
  busy?: boolean;
  savingPermissions?: boolean;
  isWorkspaceAdmin?: boolean;
  onBack: () => void;
  onPress: (connector: Connector) => void;
  onToggleWorkspace: (connector: Connector, enabled: boolean) => void;
  onPermissionChange: (connector: Connector, toolName: string, permission: ToolPermission) => void;
  onPermissionChangeAll: (connector: Connector, permission: ToolPermission) => void;
  /** Connect a further account on a connector that already has one. */
  onAddAccount?: (connector: Connector) => void;
  /** Disconnect one named account, addressed by its public connection id. */
  onDisconnectAccount?: (connector: Connector, connectionId: string) => void;
  /** Why the last connect attempt did not start — a blocked pop-up, a server that is not configured. */
  error?: string | null;
  children?: React.ReactNode;
}

const ConnectorDetail: React.FC<ConnectorDetailProps> = ({
  connector,
  projectId,
  busy,
  onAddAccount,
  onDisconnectAccount,
  savingPermissions,
  isWorkspaceAdmin,
  onBack,
  onPress,
  onToggleWorkspace,
  onPermissionChange,
  onPermissionChangeAll,
  error,
  children,
}) => {
  const Icon = iconFor(connector.icon);
  const action = actionFor(connector, projectId);
  // Joylo Sign-in has nothing to set up: the builder adds it to any app that needs sign-in
  const automatic = connector.key === "joylo_sso";
  const unavailable = connector.available === false;
  const tools = connector.tools ?? [];
  const accounts = connector.accounts ?? [];
  // Only account-scoped connectors can hold more than one, and only while the workspace allows it.
  // A project-scoped connector like Stripe has no second account to add.
  const canAddAccount =
    Boolean(onAddAccount) && connector.scope === "account" && !unavailable;

  return (
    <div className="space-y-3.5">
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          Connectors
        </button>
        <span aria-hidden="true">/</span>
        <span className="font-medium text-foreground">{connector.name}</span>
      </nav>

      <header className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3.5 dark:border-white/10 dark:bg-white/5">
        <div className="flex min-w-0 items-start gap-3">
          <div
            className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
              connector.connected ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400" : "bg-gray-100 text-muted-foreground dark:bg-white/10 dark:text-gray-400"
            }`}
          >
            <Icon className="h-7 w-7" />
            {connector.connected && (
              <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500 dark:border-card" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-foreground">
              {connector.name}
              {connector.beta && (
                <Badge
                  variant="secondary"
                  className="bg-soft px-1.5 py-0 text-[10px] font-medium text-muted-foreground hover:bg-soft dark:bg-white/10 dark:text-gray-300 dark:hover:bg-white/10"
                >
                  Beta
                </Badge>
              )}
              {connector.scope === "project" && (
                <Badge
                  variant="secondary"
                  className="bg-gray-100 px-1.5 py-0 text-[10px] font-medium text-muted-foreground hover:bg-gray-100 dark:bg-white/10 dark:text-gray-400 dark:hover:bg-white/10"
                >
                  Per project
                </Badge>
              )}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {unavailable
                ? "Disabled for this workspace"
                : connector.connected
                  ? connector.accountLabel || connector.detail || "Connected"
                  : automatic
                    ? "Works automatically"
                    : connector.detail || "Not connected"}
            </p>
          </div>
        </div>

        {isWorkspaceAdmin && connector.workspaceGoverned !== false && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            className="shrink-0 gap-1.5"
            onClick={() => onToggleWorkspace(connector, unavailable)}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Power className="h-3.5 w-3.5" />}
            {unavailable ? "Enable for workspace" : "Disable for workspace"}
          </Button>
        )}
      </header>

      <section className="rounded-xl border border-gray-200 bg-white p-3.5 dark:border-white/10 dark:bg-white/5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-semibold text-foreground">Overview</h4>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{connector.summary}</p>
            {connector.docsHint && (
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">{connector.docsHint}</p>
            )}
          </div>

          {!automatic && (
            <Button
              size="sm"
              variant={connector.connected ? "outline" : "default"}
              disabled={busy || unavailable}
              className={`shrink-0 gap-1.5 ${
                connector.connected || unavailable ? "" : "bg-ink-blue-900 text-white hover:bg-ink-blue-800 dark:bg-white/10 dark:hover:bg-white/20"
              }`}
              onClick={() => onPress(connector)}
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {connector.connected ? (connector.scope === "project" && !projectId ? "Connect a project" : "Manage") : ctaLabelFor(connector)}
              {!connector.connected && !busy && !unavailable && <ArrowRight className="h-3.5 w-3.5" />}
            </Button>
          )}
        </div>

        {error && (
          <p className="mt-2.5 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-[11px] leading-relaxed text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </p>
        )}

        {action.kind === "blocked" && (
          <p className="mt-2.5 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-relaxed text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{action.reason}</span>
          </p>
        )}

        {automatic ? (
          !unavailable && <p className="mt-2.5 flex items-start gap-2 rounded-md border border-line bg-soft px-2.5 py-2 text-[11px] leading-relaxed text-muted-foreground">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
            <span>
              Nothing to set up. When an app you build needs sign-in, Joylo adds Google and GitHub sign-in to it automatically, with no OAuth app or keys on your side.
            </span>
          </p>
        ) : action.kind === "prompt" && (
          <p className="mt-2.5 text-[11px] text-muted-foreground">
            Set up per project — we'll start one for you with this requested.
          </p>
        )}

        {connector.warnings.map((w) => (
          <p
            key={w}
            className="mt-2.5 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-relaxed text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{w}</span>
          </p>
        ))}
      </section>

      {/* Every connected account, not just the first. Until this existed a second Gmail was
          invisible here and "Disconnect" had two possible meanings — the screen showed one address
          and offered one button while two mailboxes were connected. */}
      {accounts.length > 0 && (
        <section className="rounded-xl border border-gray-200 p-3 dark:border-white/10">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <h3 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {accounts.length === 1 ? "Connected account" : `Connected accounts (${accounts.length})`}
            </h3>
            {canAddAccount && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                className="h-7 shrink-0 gap-1.5 px-2 text-[11px]"
                onClick={() => onAddAccount?.(connector)}
              >
                <Plus className="h-3 w-3" />
                Add another account
              </Button>
            )}
          </div>

          <ul className="mt-2 space-y-1.5">
            {accounts.map((account) => (
              <li
                key={account.connectionId}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 px-2.5 py-2 dark:border-white/10"
              >
                <span className="min-w-0 flex-1 truncate text-xs text-foreground">
                  {account.label}
                </span>
                {account.isDefault && accounts.length > 1 && (
                  <Badge
                    variant="secondary"
                    className="bg-gray-100 px-1.5 py-0 text-[10px] font-medium text-muted-foreground hover:bg-gray-100 dark:bg-white/10 dark:text-gray-400"
                  >
                    Default
                  </Badge>
                )}
                {account.needsReconnect ? (
                  <Badge
                    variant="secondary"
                    className="bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-700 hover:bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400"
                  >
                    Needs reconnecting
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    className="gap-1 bg-emerald-50 px-1.5 py-0 text-[10px] font-medium text-emerald-700 hover:bg-success-bg dark:text-emerald-400"
                  >
                    <Check className="h-2.5 w-2.5" />
                    Connected
                  </Badge>
                )}
                {onDisconnectAccount && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    aria-label={`Disconnect ${account.label}`}
                    className="h-7 shrink-0 px-2 text-[11px] text-red-700 hover:bg-red-50 hover:text-red-800 dark:text-red-400 dark:hover:bg-red-950/30"
                    onClick={() => onDisconnectAccount(connector, account.connectionId)}
                  >
                    Disconnect
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {children}

      <ToolPermissions
        tools={tools}
        busy={savingPermissions}
        disabled={unavailable}
        onChange={(name, permission) => onPermissionChange(connector, name, permission)}
        onChangeAll={(permission) => onPermissionChangeAll(connector, permission)}
      />

      {/* Said plainly rather than left blank: an empty permissions area reads as a loading bug */}
      {tools.length === 0 && (
        <p className="flex items-center gap-1.5 rounded-lg border border-dashed border-gray-200 px-3 py-2.5 text-[11px] text-muted-foreground dark:border-white/10 dark:text-gray-500">
          <Check className="h-3 w-3 shrink-0 text-success" />
          {connector.name} has no agent actions yet, so there is nothing to permit.
        </p>
      )}
    </div>
  );
};

export default ConnectorDetail;
